import { combineReducers } from "@reduxjs/toolkit";
import { withReduxStateSync } from "redux-state-sync";
import appReducer, { resetAppSlice } from "store/features/app/appSlice";
import arpsReducer, { resetArpsSlice } from "store/features/arps/arpsSlice";
import channelReducer, { resetChannelSlice } from "store/features/channel/channelSlice";
import dataTableReducer from "store/features/dataTable/dataTableSlice";
import normalizeByReducer, {
  resetNormalizeBySlice
} from "store/features/normalizeBy/normalizeBySlice";
import projectReducer, { resetProjectSlice } from "store/features/project/projectSlice";
import documentationReducer from "store/features/userDocumentation/documentationSlice";
import userSettingReducer, {
  resetUserSettingsSlice
} from "store/features/userSettings/userSettingsSlice";
import { useFilterLandingBinStore } from "stores/filterLandingBinStore";
import { useFilterScheduledStore } from "stores/filterScheduledStore";
import { useFocusColorHoldStore } from "stores/focusColorHoldStore";
import { useForecastStore } from "stores/forecast";
import { useMapStore } from "stores/map";
import { useMapViewFollowUpStore } from "stores/mapViewFollowUpStore";
import { useOthersMembersAvailabilityStore } from "stores/othersMembersAvailabilityStore";
import { useWellListOpenStore } from "stores/wellListOpenStore";
import { useXdaFilterRevertStore } from "stores/xdaFilterRevertStore";

import { resetFreshSessionMaxBinDefaultAppliedFlag } from "hooks/useFreshSessionMaxBinDefault";

import { useTourBridgeStore } from "components/tour/bridges/tourBridgeStore";
import { useTourStore } from "components/tour/tourStore";
import { useVisDimensionsStore } from "components/vis/visDimensionsStore";

import authReducer from "./features/auth/authSlice";
import chartsReducer, { reset as resetCharts } from "./features/charts/chartSlice";
import filesReducer from "./features/files/filesSlice";
import facilityFilterReducer, {
  resetFacilityFilterSlice
} from "./features/filter/facilityFilterSlice";
import filterReducer, { resetFilterSlice } from "./features/filter/filterSlice";
import groupByReducer, {
  resetGlobalGroupByRestoredFromSessionFlag,
  resetGroupBySlice
} from "./features/groupBy/groupBySlice";
import mapReducer, { resetMapSlice } from "./features/map/mapSlice";
import mlReducer, { resetMLSlice } from "./features/ml/mlSlice";
import organizationSettingsReducer, {
  resetOrgSettingsSlice
} from "./features/organizationSettings/organizationSettingsSlice";

const rootReducer = combineReducers({
  organizationSettings: organizationSettingsReducer,
  files: filesReducer,
  filter: filterReducer,
  facilityFilter: facilityFilterReducer,
  auth: authReducer,
  groupBy: groupByReducer,
  map: mapReducer,
  channel: channelReducer,
  userSetting: userSettingReducer,
  arps: arpsReducer,
  project: projectReducer,
  normalizeBy: normalizeByReducer,
  documentation: documentationReducer,
  dataTable: dataTableReducer,
  app: appReducer,
  charts: chartsReducer,
  ml: mlReducer
});

export const resetSlices = (dispatch) => {
  dispatch(resetAppSlice());
  dispatch(resetArpsSlice());
  dispatch(resetChannelSlice());
  dispatch(resetNormalizeBySlice());
  dispatch(resetProjectSlice());
  dispatch(resetUserSettingsSlice());
  dispatch(resetFacilityFilterSlice());
  dispatch(resetFilterSlice());
  dispatch(resetGroupBySlice());
  dispatch(resetMapSlice());
  dispatch(resetOrgSettingsSlice());
  dispatch(resetCharts());
  dispatch(resetMLSlice());
  useForecastStore.getState().reset();
  useXdaFilterRevertStore.getState().reset();
  useVisDimensionsStore.getState().reset();
  // Global Zustand store — clear on sign-out so in-memory map state (incl. the
  // rig/frac Color By drill-in) never leaks to the next user.
  useMapStore.getState().reset();
  // Same reason: the rig/frac focus colours held here are derived from the
  // previous user's legend and org palette.
  useFocusColorHoldStore.getState().reset();
  // A map-view run the last user's filter owed means nothing to the next one.
  useMapViewFollowUpStore.getState().reset();
  // Likewise a filter change the last user left waiting out the debounce.
  useFilterScheduledStore.getState().reset();
  // Same for a one-well open the last user's list armed.
  useWellListOpenStore.getState().reset();
  // And bins or a legend hold staged for a filter the last user never landed.
  useFilterLandingBinStore.getState().reset();
  // A 404 from the last session doesn't mean the endpoint is still unavailable now.
  useOthersMembersAvailabilityStore.getState().reset();
  // Sign-out is a route change, not a reload, so both of these outlive it.
  // A stale `requestCount` reopens the walkthrough picker for whoever signs
  // in next, and the bridges' projections still name the last user's project.
  useTourStore.getState().reset();
  useTourBridgeStore.getState().reset();
  // Lives outside any store (a module-level flag, not state) -- without this
  // the next user signed in on the same tab never gets their own
  // fresh-session Max Bins default (EVA-306).
  resetFreshSessionMaxBinDefaultAppliedFlag();
  // Same reason: a module-level const would freeze the first session's answer for
  // the next sign-in on this tab.
  resetGlobalGroupByRestoredFromSessionFlag();
};
export type RootState = ReturnType<typeof rootReducer>;

export default withReduxStateSync(rootReducer);
